import type { ReactNode } from 'react';
import { Icone } from './Icone.tsx';

type Tom = 'sucesso' | 'alerta' | 'erro' | 'info' | 'neutro' | 'laranja';

export function Selo({
  tom = 'neutro',
  ponto,
  children,
}: {
  tom?: Tom;
  /** Ponto colorido à esquerda: distingue o selo sem depender só do fundo. */
  ponto?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`selo selo--${tom}`}>
      {ponto && <span className="selo__ponto" />}
      {children}
    </span>
  );
}

export function Etiqueta({ solida, children }: { solida?: boolean; children: ReactNode }) {
  return <span className={`etiqueta${solida ? ' etiqueta--solida' : ''}`}>{children}</span>;
}

const ICONE_AVISO = {
  info: Icone.Info,
  alerta: Icone.Alerta,
  erro: Icone.Erro,
  sucesso: Icone.Sucesso,
};

export function Aviso({
  tipo = 'info',
  children,
}: {
  tipo?: 'info' | 'alerta' | 'erro' | 'sucesso';
  children: ReactNode;
}) {
  const Glifo = ICONE_AVISO[tipo];
  return (
    // `alert` só para erro: os demais não devem interromper o leitor de tela.
    <div className={`aviso aviso--${tipo}`} role={tipo === 'erro' ? 'alert' : 'status'}>
      <span className="aviso__icone">
        <Glifo tamanho={17} />
      </span>
      <div className="aviso__corpo">{children}</div>
    </div>
  );
}

export function Cartao({
  marcado,
  interativo,
  titulo,
  acao,
  className = '',
  children,
}: {
  marcado?: boolean;
  interativo?: boolean;
  titulo?: ReactNode;
  acao?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const classes = [
    'cartao',
    marcado && 'cartao--marcado',
    interativo && 'cartao--interativo',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes}>
      {(titulo || acao) && (
        <header className="cartao__cabecalho">
          {titulo && <h3 className="cartao__titulo">{titulo}</h3>}
          {acao}
        </header>
      )}
      {children}
    </section>
  );
}

/** Controle segmentado. Uma escolha entre poucas, sempre visíveis. */
export function Abas<T extends string>({
  opcoes,
  valor,
  aoMudar,
  rotulo,
}: {
  opcoes: { valor: T; rotulo: string; contagem?: number }[];
  valor: T;
  aoMudar: (v: T) => void;
  rotulo: string;
}) {
  return (
    <div className="abas" role="group" aria-label={rotulo}>
      {opcoes.map((o) => (
        <button
          key={o.valor}
          type="button"
          className="abas__item"
          aria-pressed={valor === o.valor}
          onClick={() => aoMudar(o.valor)}
        >
          {o.rotulo}
          {o.contagem !== undefined && o.contagem > 0 && (
            <span className="abas__contagem">{o.contagem}</span>
          )}
        </button>
      ))}
    </div>
  );
}

export function Carregando({ texto = 'Carregando' }: { texto?: string }) {
  return (
    <div className="estado-vazio" role="status">
      <span className="girador" style={{ fontSize: '1.5rem' }} />
      <span>{texto}…</span>
    </div>
  );
}

export function Vazio({
  titulo,
  children,
  acao,
}: {
  titulo?: string;
  children?: ReactNode;
  acao?: ReactNode;
}) {
  return (
    <div className="estado-vazio">
      <span className="estado-vazio__icone">
        <Icone.Vazio tamanho={22} />
      </span>
      {titulo && <strong style={{ color: 'var(--texto)' }}>{titulo}</strong>}
      {children && <p style={{ maxWidth: '32ch' }}>{children}</p>}
      {acao}
    </div>
  );
}

/** Placeholder com a forma do conteúdo, em vez de um spinner solto. */
export function Esqueleto({
  linhas = 3,
  altura = '1rem',
}: {
  linhas?: number;
  altura?: string;
}) {
  return (
    <div className="pilha-2" aria-hidden="true">
      {Array.from({ length: linhas }, (_, i) => (
        <div
          key={i}
          className="esqueleto"
          style={{ height: altura, width: i === linhas - 1 ? '60%' : '100%' }}
        />
      ))}
    </div>
  );
}

/** Par rótulo/valor usado nas revisões e fichas. */
export function Dado({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div
      className="linha linha-entre"
      style={{ alignItems: 'baseline', gap: 'var(--gap-4)', flexWrap: 'nowrap' }}
    >
      <span className="texto-fraco" style={{ flexShrink: 0 }}>
        {titulo}
      </span>
      {/* Linha pontilhada ligando rótulo e valor: guia o olho em listas longas. */}
      <span
        aria-hidden="true"
        style={{
          flex: 1,
          borderBottom: '1px dotted var(--borda)',
          transform: 'translateY(-0.25em)',
        }}
      />
      <span style={{ textAlign: 'right', minWidth: 0, wordBreak: 'break-word' }}>
        {children || '—'}
      </span>
    </div>
  );
}

/** Cartão de número grande para os painéis. */
export function Estatistica({
  titulo,
  valor,
  detalhe,
  tom,
}: {
  titulo: string;
  valor: string;
  detalhe?: string;
  tom?: 'laranja' | 'sucesso' | 'alerta';
}) {
  const cor =
    tom === 'laranja'
      ? 'var(--laranja-texto)'
      : tom === 'sucesso'
        ? 'var(--sucesso)'
        : tom === 'alerta'
          ? 'var(--alerta)'
          : 'var(--texto)';

  return (
    <div className={`cartao${tom === 'laranja' ? ' cartao--marcado' : ''}`}>
      <div className="texto-fraco">{titulo}</div>
      <div className="numeral" style={{ color: cor, marginTop: 'var(--gap-2)' }}>
        {valor}
      </div>
      {detalhe && (
        <div className="texto-fraco" style={{ marginTop: 'var(--gap-1)' }}>
          {detalhe}
        </div>
      )}
    </div>
  );
}
